import api from './api';
import { stripNestedMongoIds } from '../utils/apiPayload';
import subtournamentService from './subtournamentService';
import { t as tr } from '../i18n';
import type { PlaceLinks } from './placesService';

import type { ListingViewer, AdminPermissionEntry } from '../utils/listingAreas';
/** Ein Startgeld mit Bezeichnung (z. B. „Frauen“, „Jugend“); leere Bezeichnung = Standard */
export interface EntryFeeItem {
  label: string;
  amount: number;
  /** Feste Kennung (vom Backend vergeben, 13.10.2026) – Anmeldungen zeigen darauf */
  id?: string | null;
  /** Höchstzahl Anmeldungen für dieses Startgeld, null = unbegrenzt (13.10.2026) */
  maxEntries?: number | null;
}

export interface Tournament {
  /** Orts-Verlinkung (Detailantwort, 2026-09-20): Hub „Darten in <Stadt>“ + Kategorie-Seite – nullable */
  placeLinks?: PlaceLinks | null;
  // WICHTIG: ALLES vom Backend muss nullable sein für maximale Robustheit!
  _id?: string;
  id?: string;
  name?: string;
  slug?: string;
  description?: string;
  image?: string;
  images?: string[];
  
  // Terminierung
  dates?: number[]; // Timestamps
  start?: DartCloudTime;
  doorsOpen?: DartCloudTime;
  
  // Anmeldung
  entryEnabled?: boolean;
  entryLimit?: number;
  entryFee?: number;
  /** Mehrere Startgelder mit Bezeichnung (2026-09-26); entryFee = das erste (Standard). ADDITIV. */
  entryFees?: EntryFeeItem[] | null;
  /** Zahlungsinformationen inkl. PayPal.me (22.10.2026, additiv) */
  paymentInfo?: import('./entryService').PaymentInfo | null;
  entryDeadline?: number;
  entryCode?: string;
  /** Anmelde-Passwort nötig (2026-10-06, additiv) */
  entryCodeRequired?: boolean | null;
  /** Anmelde-Passwort im Klartext – nur für Verwalter in der Antwort (2026-10-06) */
  entryCodePlain?: string | null;
  entryLink?: string;
  entryTime?: DartCloudTime;
  entryText?: string;
  entryTextConfirmation?: string;
  entryTextPaid?: string;
  // Anmeldungs-Einstellungen (wie Flutter tournament_model, alle nullable)
  registrationType?: string | null; // 'DARTCLOUD' | 'EXTERNAL' | 'NONE'
  waitingEnabled?: boolean | null;
  requireConfirmation?: boolean | null;
  showEntryList?: boolean | null;
  showWaitingList?: boolean | null;
  leaveEntryAllowed?: boolean | null;
  // 2026-10-24 (Marcel): Anmeldung während Turnierbetrieb + internes Turnier; Backend liefert zusätzlich den Betriebsstatus (alle additiv/nullable)
  registrationDuringOperation?: boolean | null;
  registrationInternal?: boolean | null;
  operationRunning?: boolean | null;
  registrationClosedReason?: 'OPERATION_RUNNING' | string | null;
  entryChangeableUntil?: number | null;
  entryBirthdayRequired?: boolean | null;
  entryEmailRequired?: boolean | null;
  entryPhoneRequired?: boolean | null;
  entryDeadlineOnSite?: DartCloudTime | null;
  teamSize?: number | null;
  teamSizeMax?: number | null;
  boards?: number | null;
  hasCompetitions?: boolean | null;
  
  // Kategorien & Filter
  categories?: string[]; // ['steeldart', 'edart', 'online']
  tags?: string[];
  gameMode?: string;
  type?: string; // TournamentTypes
  
  // Location
  address?: Address;
  country?: string;
  
  // Status & Meta
  visible?: boolean;
  verified?: boolean;
  cancelled?: boolean;
  spam?: boolean;
  featured?: boolean;
  sponsored?: boolean;
  views?: number;
  
  // Admin & Owner
  owner?: string;
  admins?: string[];
  /** 2026-10-05 (additiv, nullable): Rechte des Betrachters – Bereiche je Admin, siehe utils/listingAreas */
  viewer?: ListingViewer | null;
  adminPermissions?: AdminPermissionEntry[] | null;
  createdBy?: string;
  createdAt?: number;
  updatedAt?: number;
  
  // Contact Info
  phone?: string;
  email?: string;
  
  // Custom Fields
  customEntryFields?: CustomEntryField[];
  customEntryHint?: string;

  // E-Mail-Vorlagen für Anmeldungen (wie Flutter tournament_model, nullable)
  emailTemplates?: Record<string, { subject?: string | null; body?: string | null; enabled?: boolean | null } | null> | null;
  
  // Verknüpfungen (2026-09-19): aufgelöste Einträge (Verein/Spielstätte/Shop/
  // Liga/Verband), auf deren Seiten das Turnier erscheint – alle nullable
  linkedListings?: LinkedEntity[] | null;
  /** Serien-Id (nur bei Einzelturnieren einer Turnierserie) */
  series?: string | null;
  /** Aufgelöste Serie (nur gesetzt, wenn das Turnier zu einer Serie gehört) */
  seriesInfo?: LinkedSeriesInfo | null;
  /** Batch-Id der Bulk-Erstellung (ein Termin = ein Turnier) */
  bulkBatchId?: string | null;
  /** Fortschritt im Einrichtungs-Guide (2026-09-20, nullable) */
  setupGuide?: {
    lastStep?: string | null;
    completedSteps?: string[] | null;
    completedAt?: number | null;
    updatedAt?: number | null;
  } | null;

  // Berechnete Felder
  isToday?: boolean;
  isValid?: boolean;
  validAddress?: boolean;
}

/** Typ eines verknüpfbaren Eintrags (wie Backend-Enum). */
export type LinkedEntityType = 'CLUB' | 'PUB' | 'SHOP' | 'LEAGUE' | 'ASSOCIATION';

/** Aufgelöster verknüpfter Eintrag aus GET /api/tournament/... (alles nullable). */
export interface LinkedEntity {
  type?: LinkedEntityType | string | null;
  id?: string | null;
  name?: string | null;
  slug?: string | null;
  image?: string | null;
}

/** Aufgelöste Turnierserie eines Einzelturniers (alles nullable). */
export interface LinkedSeriesInfo {
  id?: string | null;
  name?: string | null;
  slug?: string | null;
  image?: string | null;
}

/** Verknüpfung im Payload (POST /api/tournament, /update, /tournaments/bulk). */
export interface LinkedListingRef {
  type: LinkedEntityType;
  id: string;
}

/**
 * Kompaktes Turnier aus dem Feld `linkedTournaments` der Detailantworten von
 * Verein/Spielstätte/Shop/Liga/Verband/Serie (alles nullable).
 */
export interface LinkedTournament {
  id?: string | null;
  slug?: string | null;
  name?: string | null;
  dates?: number[] | null;
  image?: string | null;
  /** Galerie (2026-10-05, additiv): erstes Bild als Rückgriff ohne Plakat */
  images?: string[] | null;
  type?: string | null;
  categories?: string[] | null;
  address?: Address | null;
  cancelled?: boolean | null;
  hasCompetitions?: boolean | null;
  series?: string | null;
  registrationType?: string | null;
  entryEnabled?: boolean | null;
  entryLink?: string | null;
  trophies?: any[] | null;
}

/** Namensschema der Bulk-Erstellung (ein Termin = ein Turnier). */
export type BulkNamingScheme = 'same' | 'date' | 'number';

/** Payload für POST /api/tournaments/bulk (Turnierfelder + Bulk-Optionen). */
export interface BulkCreatePayload {
  [key: string]: any;
  dates: number[];
  naming?: BulkNamingScheme;
  createSeries?: boolean;
  competitions?: Array<{ name: string; type: string; categories: string[]; [key: string]: any }>;
  linkedListings?: LinkedListingRef[];
}

/** Antwort von POST /api/tournaments/bulk (alles nullable geparst). */
export interface BulkCreateResult {
  created?: Array<{ id?: string | null; slug?: string | null; name?: string | null; dates?: number[] | null }> | null;
  failed?: Array<{ id?: string | null; name?: string | null; date?: number | null; error?: string | null }> | null;
  count?: number | null;
  batchId?: string | null;
  series?: { id?: string | null; slug?: string | null; name?: string | null } | null;
}

export interface DartCloudTime {
  hour?: number;
  minute?: number;
}

export interface Address {
  street?: string;
  houseNumber?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  placeId?: string;
  label?: string; // Oft vom Backend als vollständige Adresse
}

export interface CustomEntryField {
  name?: string;
  type?: string;
  required?: boolean;
  options?: any[]; // Backend: [{ id?, label, value? }] — historisch auch string[]
  // Erweiterte Feld-Eigenschaften (wie Flutter custom_entry_field_schema, alle nullable)
  id?: string | null;
  perPlayer?: boolean | null;
  /** Nur bei diesen Startgeld-Kennungen (Marcel 2026-10-15), leer/fehlend = immer */
  entryFeeIds?: string[] | null;
  description?: string | null;
  placeholder?: string | null;
  min?: number | null;
  max?: number | null;
  maxLength?: number | null;
  order?: number | null;
}

export interface TournamentResponse {
  success: boolean;
  data: Tournament[];
  total?: number;
  page?: number;
  pages?: number;
  /** rohe Anzahl der Serverantwort vor dem Bereinigen (22.10.2026) */
  rawCount?: number;
  /** Gesamtzahl laut Server (22.10.2026) */
  serverTotal?: number | null;
}

export interface TournamentDetailResponse {
  success: boolean;
  data: Tournament;
}

export interface TournamentFilters {
  /** Sortierung der paginierten Liste (2026-10-06): 'newest' = zuletzt angelegte zuerst; ohne = Server-Reihenfolge */
  sort?: 'newest' | 'oldest';
  search?: string;
  categories?: string[]; // ['steeldart', 'edart', 'online']
  dateFilter?: 'all' | 'today' | 'week' | 'month' | 'custom';
  customDate?: string; // YYYY-MM-DD
  entryFeeRange?: {
    min: number;
    max: number;
  };
  location?: {
    lat: number;
    lng: number;
    radius?: number; // km
  };
  sortBy?: 'quality' | 'date' | 'created';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

// Mehrfachauswahl (Marcel 2026-10-06)
export interface BulkUpdateSet {
  description?: string | null; categories?: string[]; gameMode?: string | null; boards?: number | null; email?: string | null; phone?: string | null;
  tags?: string[] | null; trophies?: any; doorsOpen?: DartCloudTime | null; start?: DartCloudTime | null;
  entryEnabled?: boolean; entryDeadline?: number | null; entryFees?: EntryFeeItem[] | null; cancelled?: boolean;
  /** 2026-10-08: Sichtbarkeit (Entwurf ↔ sichtbar) für alle gewählten Turniere */
  visible?: boolean;
  image?: string; removeImage?: boolean; linkedListings?: { type: string; id: string }[] | null;
  /** 2026-10-08: Galerie-Bilder (Base64) an alle gewählten Turniere anhängen */
  addImages?: string[];
}
export interface BulkUpdatePayload { ids: string[]; set?: BulkUpdateSet; templates?: { registration?: string | null; software?: string | null } }
export interface BulkFailure { id: string; reason: string }
export interface BulkUpdateResult { updated: string[]; failed: BulkFailure[] }
export interface BulkDeleteResult { deleted: string[]; failed: BulkFailure[] }

class TournamentService {
  // Alle validen Turniere abrufen
  /**
   * Alle sichtbaren Turniere aus dem Server-Cache (unpaginierte Antwort = nacktes Array, wie die App).
   * Für Sektionen, die ALLE Turniere brauchen (z. B. kommende nach Termin) – die paginierte Liste mit
   * limit 500 schnitt bei > 500 Turnieren ab (2026-10-06).
   */
  async getAllTournamentsCached(): Promise<Tournament[]> {
    const response = await api.get('/api/tournaments');
    const d = response?.data;
    return Array.isArray(d) ? d : (Array.isArray(d?.data) ? d.data : []);
  }

  async getTournaments(filters?: TournamentFilters): Promise<TournamentResponse> {
    try {
      const params: any = {
        // Backend (pagination-helper) erlaubt nur limit 1-500 -> 1000 liefert 400.
        // Bei Bedarf auf das Maximum (500) clampen.
        limit: Math.min(filters?.limit || 500, 500),
        offset: filters?.offset || 0
      };

      // Filter Parameter hinzufügen
      if (filters?.sort) params.sort = filters.sort;
      if (filters?.search) params.search = filters.search;
      if (filters?.categories?.length) params.categories = filters.categories.join(',');
      if (filters?.dateFilter && filters.dateFilter !== 'all') {
        params.dateFilter = filters.dateFilter;
        if (filters.dateFilter === 'custom' && filters.customDate) {
          params.date = filters.customDate;
        }
      }
      if (filters?.entryFeeRange) {
        params.entryFeeMin = filters.entryFeeRange.min;
        params.entryFeeMax = filters.entryFeeRange.max;
      }
      if (filters?.location) {
        params.lat = filters.location.lat;
        params.lng = filters.location.lng;
        params.radius = filters.location.radius || 100;
      }
      if (filters?.sortBy) params.sortBy = filters.sortBy;
      if (filters?.sortOrder) params.sortOrder = filters.sortOrder;

      const response = await api.get('/api/tournaments', { params });
      
      // Backend liefert verschiedene Formate:
      // 1. Paginated: { data: [...], total: 426, limit: 20, offset: 0 }
      // 2. Standard: [...] (direktes Array)
      
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        // Paginated Format
        const safeTournaments = this.getSafeTournamentArray(response.data.data);
        const validTournaments = this.filterValidTournaments(safeTournaments, false); // Temporarily disable date filtering
        return {
          success: true,
          data: validTournaments,
          total: validTournaments.length,
          page: Math.floor((response.data.offset || 0) / (response.data.limit || 20)) + 1,
          pages: Math.ceil(validTournaments.length / (response.data.limit || 20)),
          // 22.10.2026: rohe Seitengröße + Server-Total für die Abbruchbedingung beim seitenweisen Laden
          rawCount: safeTournaments.length,
          serverTotal: typeof response.data.total === 'number' ? response.data.total : null,
        };
      } else if (Array.isArray(response.data)) {
        // Standard Format (direktes Array)
        const safeTournaments = this.getSafeTournamentArray(response.data);
        const validTournaments = this.filterValidTournaments(safeTournaments, false); // Temporarily disable date filtering
        return {
          success: true,
          data: validTournaments,
          total: validTournaments.length,
          page: 1,
          pages: 1
        };
      } else {
        // Fallback
        console.warn('Unbekanntes Response-Format:', response.data);
        return {
          success: false,
          data: [],
          total: 0,
          page: 1,
          pages: 1
        };
      }
    } catch (error) {
      console.error('Fehler beim Abrufen der Turniere:', error);
      throw error;
    }
  }

  // Heutige Turniere abrufen
  async getTodaysTournaments(): Promise<TournamentResponse> {
    try {
      const response = await api.get('/api/tournaments/today');
      
      // Backend liefert direktes Array
      if (Array.isArray(response.data)) {
        const safeTournaments = this.getSafeTournamentArray(response.data);
        const validTournaments = this.filterValidTournaments(safeTournaments, false); // Temporarily disable date filtering
        return {
          success: true,
          data: validTournaments,
          total: validTournaments.length
        };
      }
      
      // Fallback für andere Formate
      const safeTournaments = this.getSafeTournamentArray(response.data.tournaments || response.data || []);
      const validTournaments = this.filterValidTournaments(safeTournaments, true);
      return {
        success: true,
        data: validTournaments,
        total: validTournaments.length
      };
    } catch (error) {
      console.error('Fehler beim Abrufen der heutigen Turniere:', error);
      throw error;
    }
  }

  // Turnier nach Slug abrufen
  async getTournamentBySlug(slug: string): Promise<TournamentDetailResponse> {
    try {
      const response = await api.get(`/api/tournament/slug/${slug}`);
      
      // Backend kann direktes Objekt oder { tournament: {...} } liefern
      const tournament = response.data.tournament || response.data;
      
      return {
        success: true,
        data: tournament && typeof tournament === 'object' ? tournament : null
      };
    } catch (error) {
      console.error('Fehler beim Abrufen des Turniers:', error);
      throw error;
    }
  }

  // Turnier nach ID abrufen
  async getTournamentById(id: string): Promise<TournamentDetailResponse> {
    try {
      const response = await api.get(`/api/tournament/id/${id}`);
      
      // Backend kann direktes Objekt oder { tournament: {...} } liefern
      const tournament = response.data.tournament || response.data;
      
      return {
        success: true,
        data: tournament && typeof tournament === 'object' ? tournament : null
      };
    } catch (error) {
      console.error('Fehler beim Abrufen des Turniers:', error);
      throw error;
    }
  }

  async createTournament(payload: any): Promise<any> {
    const res = await api.post('/api/tournament', payload);
    return res.data;
  }

  /**
   * Bulk-Erstellung (2026-09-19): jeder Termin wird ein eigenes Turnier
   * (bis 500), optional mit Namensschema, Container-Serie, Competitions je
   * Tag und Verknüpfungen. Fehler → 400 mit Textmeldung (Aufrufer zeigt sie an).
   */
  async createTournamentsBulk(payload: BulkCreatePayload): Promise<BulkCreateResult> {
    const res = await api.post('/api/tournaments/bulk', payload);
    const data = res?.data;
    return data && typeof data === 'object' ? (data as BulkCreateResult) : {};
  }

  /** Quick-Ersteller: Plakat (reines base64, ohne data:-Präfix) zur KI-Analyse. */
  async analyzePoster(base64Image: string): Promise<any> {
    const res = await api.post('/api/tournament/analyze-poster', { data: base64Image });
    return res.data;
  }

  async updateTournament(payload: any): Promise<any> {
    const res = await api.post('/api/tournament/update', stripNestedMongoIds(payload));
    return res.data;
  }

  async deleteTournament(id: string): Promise<any> {
    const res = await api.post('/api/tournament/delete', { id });
    return res.data;
  }

  /** Mehrfachauswahl (Marcel 2026-10-06): dieselben Felder auf viele Turniere; Teilergebnis statt Alles-oder-nichts */
  async bulkUpdateTournaments(payload: BulkUpdatePayload): Promise<BulkUpdateResult> {
    const res = await api.post('/api/tournaments/bulk-update', stripNestedMongoIds(payload));
    const d = res?.data;
    return { updated: Array.isArray(d?.updated) ? d.updated : [], failed: Array.isArray(d?.failed) ? d.failed : [] };
  }

  async bulkDeleteTournaments(ids: string[]): Promise<BulkDeleteResult> {
    const res = await api.post('/api/tournaments/bulk-delete', { ids });
    const d = res?.data;
    return { deleted: Array.isArray(d?.deleted) ? d.deleted : [], failed: Array.isArray(d?.failed) ? d.failed : [] };
  }

  // Helper: Bild-URL generieren (100% robust; leer/whitespace = kein Bild, data:/http unverändert)
  getImageUrl(tournament?: Tournament): string | null {
    if (!tournament) return null; // Signal für Fallback

    const resolve = (value?: string | null): string | null => {
      if (typeof value !== 'string') return null;
      const trimmed = value.trim();
      if (!trimmed) return null; // leerer String = kein Bild
      if (trimmed.startsWith('data:') || trimmed.startsWith('http')) return trimmed;
      return `${api.defaults.baseURL}${trimmed.startsWith('/') ? '' : '/'}${trimmed}`;
    };

    const fromImage = resolve(tournament.image);
    if (fromImage) return fromImage;

    if (tournament.images && Array.isArray(tournament.images) && tournament.images.length > 0) {
      return resolve(tournament.images[0]);
    }

    // Kein Bild vorhanden - return null für Fallback-Styling
    return null;
  }

  // Helper: Check if tournament has image (robust)
  hasImage(tournament?: Tournament): boolean {
    const imageUrl = this.getImageUrl(tournament);
    return imageUrl !== null;
  }

  // Helper: Datum formatieren (robust)
  formatTournamentDate(timestamp?: number): string {
    if (!timestamp || typeof timestamp !== 'number' || isNaN(timestamp)) {
      return tr('web.service.entry-service.datum-unbekannt');
    }
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return tr('web.service.tournament-service.ungueltiges-datum');
      
      return date.toLocaleDateString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return tr('web.service.entry-service.datum-unbekannt');
    }
  }

  // Helper: Zeit formatieren (robust)
  formatTime(time?: DartCloudTime): string {
    if (!time || typeof time !== 'object') return '';
    if (typeof time.hour !== 'number' || typeof time.minute !== 'number') return '';
    if (isNaN(time.hour) || isNaN(time.minute)) return '';
    
    try {
      const hour = Math.max(0, Math.min(23, Math.floor(time.hour)));
      const minute = Math.max(0, Math.min(59, Math.floor(time.minute)));
      return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    } catch (error) {
      return '';
    }
  }

  // Helper: Entry Fee formatieren (robust)
  formatEntryFee(fee?: number | null): string {
    if (fee === null || fee === undefined || typeof fee !== 'number') return tr('web.page.tournament-page.kostenlos');
    if (isNaN(fee) || fee <= 0) return tr('web.page.tournament-page.kostenlos');
    
    try {
      return `${fee.toFixed(2)}€`;
    } catch (error) {
      return tr('web.page.tournament-page.kostenlos');
    }
  }

  // Helper: Adresse formatieren (robust)
  formatAddress(address?: Address): string {
    if (!address || typeof address !== 'object') return tr('web.service.app-data-service.adresse-unbekannt');
    
    // Prüfe zuerst ob label vorhanden (oft vollständige Adresse vom Backend)
    if (address.label && typeof address.label === 'string' && address.label.trim()) {
      return address.label.trim();
    }
    
    const parts: string[] = [];
    
    try {
      if (address.street && typeof address.street === 'string' && address.street.trim()) {
        let streetPart = address.street.trim();
        if (address.houseNumber && typeof address.houseNumber === 'string' && address.houseNumber.trim()) {
          streetPart += ` ${address.houseNumber.trim()}`;
        }
        parts.push(streetPart);
      }
      
      if (address.zipCode && address.city && 
          typeof address.zipCode === 'string' && typeof address.city === 'string' &&
          address.zipCode.trim() && address.city.trim()) {
        parts.push(`${address.zipCode.trim()} ${address.city.trim()}`);
      } else if (address.city && typeof address.city === 'string' && address.city.trim()) {
        parts.push(address.city.trim());
      }
      
      if (parts.length === 0) {
        return address.country && typeof address.country === 'string' && address.country.trim() 
          ? address.country.trim() 
          : tr('web.service.app-data-service.adresse-unbekannt');
      }
      
      return parts.join(', ');
    } catch (error) {
      return tr('web.service.app-data-service.adresse-unbekannt');
    }
  }

  // Helper: Turniere nach Kategorie filtern
  filterByCategory(tournaments: Tournament[], categories: string[]): Tournament[] {
    if (!tournaments || !Array.isArray(tournaments)) return [];
    if (!categories.length) return tournaments;
    return tournaments.filter(tournament => {
      if (!tournament.categories || !Array.isArray(tournament.categories)) return false;
      return categories.some(category => tournament.categories!.includes(category));
    });
  }

  // Helper: Gesponserte Turniere extrahieren
  getSponsoredTournaments(tournaments: Tournament[]): Tournament[] {
    if (!tournaments || !Array.isArray(tournaments)) return [];
    return tournaments
      .filter(tournament => tournament.featured || tournament.sponsored)
      .filter(tournament => {
        if (tournament.image) return true;
        if (tournament.images && Array.isArray(tournament.images) && tournament.images.length > 0) return true;
        return false;
      })
      .sort(() => Math.random() - 0.5); // Shuffle für zufällige Reihenfolge
  }

  // Helper: Turniere mit/ohne Bilder trennen
  separateTournamentsByImages(tournaments: Tournament[]): {
    withImages: Tournament[];
    withoutImages: Tournament[];
  } {
    const withImages: Tournament[] = [];
    const withoutImages: Tournament[] = [];

    if (!tournaments || !Array.isArray(tournaments)) {
      return { withImages, withoutImages };
    }

    tournaments.forEach(tournament => {
      const hasImage = tournament.image || (tournament.images && Array.isArray(tournament.images) && tournament.images.length > 0);
      if (hasImage) {
        withImages.push(tournament);
      } else {
        withoutImages.push(tournament);
      }
    });

    return { withImages, withoutImages };
  }

  // Helper: Distanz berechnen (Haversine)
  calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371; // Erdradius in km
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  // Helper: Turniere nach Datum sortieren
  sortTournamentsByDate(tournaments: Tournament[], ascending = true): Tournament[] {
    if (!tournaments || !Array.isArray(tournaments)) return [];
    return tournaments.sort((a, b) => {
      const dateA = this.getNextTimestamp(a) || a.dates?.[0] || 0;
      const dateB = this.getNextTimestamp(b) || b.dates?.[0] || 0;
      return ascending ? dateA - dateB : dateB - dateA;
    });
  }

  /** Berechnet Dates inkl. aller Subtournament-Dates (wie Flutter calcDates). */
  calcDates(tournament?: Tournament): number[] {
    if (!tournament) return [];
    const list: number[] = [];
    const id = tournament.id || tournament._id;
    if (id) {
      const subs = subtournamentService.getByMainTournament(id);
      for (const sub of subs) {
        if (Array.isArray(sub.dates)) {
          for (const d of sub.dates) {
            if (typeof d === 'number' && !list.includes(d)) list.push(d);
          }
        }
      }
    }
    if (Array.isArray(tournament.dates)) {
      for (const d of tournament.dates) {
        if (typeof d === 'number' && !list.includes(d)) list.push(d);
      }
    }
    return list;
  }

  /** Berechnet Kategorien inkl. aller Subtournament-Kategorien (wie Flutter calcCategories). */
  calcCategories(tournament?: Tournament): string[] {
    if (!tournament) return [];
    const list: string[] = [];
    const id = tournament.id || tournament._id;
    if (id) {
      const subs = subtournamentService.getByMainTournament(id);
      for (const sub of subs) {
        if (Array.isArray(sub.categories)) {
          for (const c of sub.categories) {
            if (c && !list.includes(c)) list.push(c);
          }
        }
      }
    }
    if (Array.isArray(tournament.categories)) {
      for (const c of tournament.categories) {
        if (c && !list.includes(c)) list.push(c);
      }
    }
    return list;
  }

  /** Nächster Datums-Timestamp >= heute, sonst frühester. Inklusive Subtournament-Dates. */
  getNextTimestamp(tournament?: Tournament): number | null {
    const dates = this.calcDates(tournament);
    if (dates.length === 0) return null;
    const now = new Date();
    const todayTs = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const future = dates.filter(t => t >= todayTs).sort((a, b) => a - b);
    if (future.length > 0) return future[0];
    const all = dates.slice().sort((a, b) => a - b);
    return all.length > 0 ? all[0] : null;
  }

  /** Anzahl Tage eines Turniers (1 = eintägig). Inklusive Subtournament-Dates. */
  getDayCount(tournament?: Tournament): number {
    const dates = this.calcDates(tournament);
    if (dates.length === 0) return 0;
    const unique = new Set(
      dates.map((t) => {
        const d = new Date(t);
        return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      }),
    );
    return unique.size;
  }

  /** Kurzname Wochentag (Mo, Di, Mi, …). */
  getWeekdayShort(timestamp?: number | null): string {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '');
  }

  /** Tag als Zahl (1-31). */
  getDayNumber(timestamp?: number | null): string {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '';
    return d.getDate().toString();
  }

  /** Kurzname Monat (Jan, Feb, …). */
  getMonthShort(timestamp?: number | null): string {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '');
  }

  /** Thumbnail-URL analog zum Flutter CachedImage. Leer/whitespace → null, data:/http(s) unverändert. */
  buildThumbnailUrl(fileName?: string | null, size: 100 | 200 | 400 = 200): string | null {
    if (typeof fileName !== 'string') return null;
    const trimmed = fileName.trim();
    if (!trimmed) return null; // leerer String = kein Bild
    if (trimmed.startsWith('data:')) return trimmed;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    const base = api.defaults.baseURL || '';
    const dot = trimmed.lastIndexOf('.');
    if (dot === -1) return `${base}/api/file/${trimmed}`;
    const name = trimmed.substring(0, dot);
    const ext = trimmed.substring(dot);
    return `${base}/api/file/thumbnails/${name}_${size}_${size}_thumbnail${ext}`;
  }

  /** Bild-URL als Thumbnail (bevorzugt), inkl. Fallback auf images[0]. */
  getThumbnailUrl(tournament?: Tournament, size: 100 | 200 | 400 = 200): string | null {
    if (!tournament) return null;
    const file = (tournament.image && tournament.image.trim() ? tournament.image : null)
      || (tournament.images && Array.isArray(tournament.images) && tournament.images.length > 0 ? tournament.images[0] : null);
    return this.buildThumbnailUrl(file, size);
  }

  // Helper: Kategorie-Labels
  getCategoryLabel(category: string): string {
    const labels: Record<string, string> = {
      'steeldart': 'Steeldart',
      'edart': 'E-Dart',
      'online': 'Online'
    };
    return labels[category] || category;
  }

  // Helper: Kategorie-Farben
  getCategoryColor(category: string): string {
    const colors: Record<string, string> = {
      'steeldart': 'text-red-500',
      'edart': 'text-orange-500',
      'online': 'text-blue-500'
    };
    return colors[category] || 'text-gray-500';
  }

  // Helper: Safe Array-Zugriff für Tournament-Listen (robust)
  getSafeTournamentArray(data?: Tournament[]): Tournament[] {
    if (!data || !Array.isArray(data)) return [];
    return data.filter(
      (tournament) =>
        tournament &&
        typeof tournament === 'object' &&
        (tournament._id || tournament.id),
    );
  }

  // Helper: Check if tournament has valid dates (today or future) - like Flutter app
  hasValidDates(tournament?: Tournament): boolean {
    if (!tournament || !tournament.dates || !Array.isArray(tournament.dates)) {
      return false;
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayTimestamp = today.getTime();

    for (const timestamp of tournament.dates) {
      if (typeof timestamp === 'number' && !isNaN(timestamp)) {
        if (timestamp >= todayTimestamp) {
          return true; // Mindestens ein Datum heute oder in der Zukunft
        }
      }
    }

    return false; // Alle Daten in der Vergangenheit
  }

  // Helper: Filter expired tournaments (like Flutter app does)
  filterValidTournaments(tournaments: Tournament[], filterExpired: boolean = true): Tournament[] {
    if (!tournaments || !Array.isArray(tournaments)) return [];

    return tournaments.filter(tournament => {
      if (!tournament || typeof tournament !== 'object') return false;

      // Nur explizit unsichtbare oder abgesagte Turniere ausschließen
      if (tournament.visible === false) return false;
      if (tournament.cancelled === true) return false;
      if ((tournament as any).spam === true) return false;

      // Datum-Filter (wie in Flutter App)
      if (filterExpired && !this.hasValidDates(tournament)) {
        return false;
      }

      return true;
    });
  }
}

export default new TournamentService();